// src/pages/TextTools/MotivationLetter.jsx
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { generateMotivationLetter, checkMotivationLetterStatus, downloadMotivationLetter } from '../../services/chatApi';
import { Download, FileText, Loader2, Upload, Copy, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { Document, Packer, Paragraph, TextRun } from 'docx';

const SubscribePrompt = () => {
    const { t } = useTranslation();
    const PLAN_PRICE = "14,90 €";

    return (
        <div className="flex items-center justify-center h-full p-4">
            <div className="max-w-md w-full bg-brand-dark border-2 border-brand-orange rounded-lg p-8 shadow-lg">
                <h2 className="text-center text-3xl font-bold text-brand-orange mb-4">
                    {PLAN_PRICE}/{t('subscribePrompt.perMonth')}
                </h2>
                <ul className="text-slate-200 space-y-3 list-disc list-inside mb-8">
                    <li>{t('subscribePrompt.feature1')}</li>
                    <li>{t('subscribePrompt.feature2')}</li>
                    <li>{t('subscribePrompt.feature3')}</li>
                    <li>{t('subscribePrompt.feature4')}</li>
                    <li>{t('subscribePrompt.feature5')}</li>
                </ul>
                <Link 
                    to="/pricing"
                    className="w-full block text-center px-6 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
                >
                    {t('subscribePrompt.subscribeButton')}
                </Link>
            </div>
        </div>
    );
};

const PurchaseCreditsPrompt = () => {
    const { t } = useTranslation();
    const { user } = useAuth();

    return (
        <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <h2 className="text-2xl font-semibold mb-2 text-slate-800">{t('purchaseCreditsPrompt.title')}</h2>
            <p className="text-slate-800 mb-1">{t('purchaseCreditsPrompt.yourCredits')} <span className="text-brand-orange font-bold">{user?.credits || 0}</span></p>
            <p className="text-slate-800 mb-6 max-w-md">{t('purchaseCreditsPrompt.description')}</p>
            <Link 
                to="/purchase-credits"
                className="px-8 py-3 bg-brand-orange text-white font-semibold rounded-md hover:bg-orange-600 transition-colors"
            >
                {t('purchaseCreditsPrompt.purchaseButton')}
            </Link>
        </div>
    );
};

const MotivationLetter = () => {
    const { t } = useTranslation();
    const { user, setCredits } = useAuth();
    const [uploadedResume, setUploadedResume] = useState(null);
    const [jobDescription, setJobDescription] = useState('');
    const [generatedLetter, setGeneratedLetter] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [taskId, setTaskId] = useState(null);
    const [taskStatus, setTaskStatus] = useState(null);
    const [copied, setCopied] = useState(false);

    const [additionalInstructions, setAdditionalInstructions] = useState('');

    // Effect to poll task status when taskId is set
    useEffect(() => {
        let interval = null;
        if (taskId && taskStatus === 'processing') {
            interval = setInterval(async () => {
                try {
                    const statusResponse = await checkMotivationLetterStatus(taskId);
                    setTaskStatus(statusResponse.status);

                    if (statusResponse.status === 'completed') {
                        setGeneratedLetter(statusResponse.cover_letter);
                        clearInterval(interval);
                        
                        // Update credits if returned
                        if (statusResponse.new_credits !== undefined) {
                            console.log(`Credits updated. New balance: ${statusResponse.new_credits}`);
                            setCredits(statusResponse.new_credits);
                        }
                        
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                    } else if (statusResponse.status === 'failed') {
                        setError(statusResponse.error || t('motivationLetter.errorGeneric'));
                        clearInterval(interval);
                        setTaskId(null);
                        setIsLoading(false);
                        setTaskStatus(null);
                    }
                } catch (err) {
                    console.error('Error checking task status:', err);
                    clearInterval(interval);
                    setTaskId(null);
                    setError(t('motivationLetter.statusCheckError'));
                    setIsLoading(false); // This stops the spinner
                    setTaskStatus(null);
                }
            }, 2000); // Check every 2 seconds
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [taskId, taskStatus, t, setCredits]);

    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 10 * 1024 * 1024) {
                setError(t('motivationLetter.fileTooLarge'));
                return;
            }
            setUploadedResume(file);
            setError(null);
        }
    };

    const handleGenerate = async () => {
        setError(null);
        setGeneratedLetter('');
        setTaskStatus(null);
        setTaskId(null);

        if (!uploadedResume) {
            setError(t('motivationLetter.uploadResumeFirst'));
            setIsLoading(false); 
            return;
        }

        if (!jobDescription.trim()) {
            setError(t('motivationLetter.enterJobDescriptionFirst'));
            setIsLoading(false); 
            return;
        }

        setIsLoading(true);

        try {
            const formData = new FormData();
            formData.append('resume', uploadedResume);
            formData.append('job_description', jobDescription);
            formData.append('additional_instructions', additionalInstructions);

            const result = await generateMotivationLetter(formData);
            
            if (result.task_id) {
                setTaskId(result.task_id);
                setTaskStatus('processing');
            } else {
                // If no task_id is returned, something went wrong
                setError(t('motivationLetter.errorGeneric'));
                setIsLoading(false);
                setTaskStatus(null);
            }
        } catch (err) {
            setError(err.message || t('motivationLetter.errorGeneric'));
            setIsLoading(false); // Add this line to stop the spinner
            setTaskStatus(null);
        }
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(generatedLetter);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            setError(t('motivationLetter.copyError'));
        }
    };

    
    const handleDownload = async (format) => {
        try {
            if (format === 'txt') {
                const blob = new Blob([generatedLetter], { type: 'text/plain' });
                downloadBlob(blob, 'txt');
            } else if (format === 'docx') {
                const doc = new Document({
                    sections: [{
                        properties: {},
                        children: [
                            new Paragraph({
                                children: generatedLetter.split('\n').flatMap((line, i) => [
                                    ...(i > 0 ? [new TextRun({ break: 1 })] : []),
                                    new TextRun(line)
                                ])
                            })
                        ],
                    }],
                });
                
                const blob = await Packer.toBlob(doc);
                downloadBlob(blob, 'docx');
            }
        } catch (err) {
            setError(t('motivationLetter.downloadError'));
        }
    };

    const downloadBlob = (blob, extension) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `motivation_letter.${extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
    };
    const handleClear = () => {
        setUploadedResume(null);
        setJobDescription('');
        setGeneratedLetter('');
        setAdditionalInstructions('');
        setError(null);
        setTaskId(null);
        setTaskStatus(null);
    };

    return (
        <div className="p-6 md:p-8 h-full overflow-y-auto text-brand-dark bg-brand-white">
            {(user?.subscription_status !== 'active' && (!user?.is_verified || user?.credits <= 2)) ? (
                <SubscribePrompt />
            ) : user?.credits <= 0 ? (
                <PurchaseCreditsPrompt />
            ) : (
                <div className="max-w-4xl mx-auto">
                    <h3 className="text-3xl font-bold text-brand-orange">
                        {t('motivationLetter.title')}
                    </h3>
                    <p className="mt-2 text-brand-dark">
                        {t('motivationLetter.description')}
                    </p>
                   

                    {/* Resume Upload Section */}
                    <div className="mt-8">
                        <label className="block text-sm font-medium mb-2">
                            {t('motivationLetter.resumeLabel')}
                        </label>
                        <div className="flex items-center space-x-4">
                            <label className="flex-1 flex items-center justify-center px-4 py-3 border-2 border-dashed border-slate-600 rounded-md cursor-pointer hover:border-brand-orange transition-colors">
                                <Upload className="mr-2" size={20} />
                                <span>{uploadedResume ? uploadedResume.name : t('motivationLetter.chooseResume')}</span>
                                <input
                                    type="file"
                                    accept=".pdf,.docx,.doc,.txt"
                                    onChange={handleFileUpload}
                                    className="hidden"
                                    disabled={isLoading}
                                />
                            </label>
                        </div>
                        <p className="text-xs text-slate-600 mt-1">
                            {t('motivationLetter.fileFormats')}
                        </p>
                    </div>

                    {/* Job Description Section */}
                    <div className="mt-6">
                        <label className="block text-sm font-medium mb-2">
                            {t('motivationLetter.jobDescriptionLabel')}
                        </label>
                        <textarea
                            value={jobDescription}
                            onChange={(e) => setJobDescription(e.target.value)}
                            placeholder={t('motivationLetter.jobDescriptionPlaceholder')}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[200px] resize-y rounded-md"
                            disabled={isLoading}
                        />
                    </div>
                    <div className="mt-6">
                        <label className="block text-sm font-medium mb-2">
                            {t('motivationLetter.additionalInstructionsLabel')}
                        </label>
                        <textarea
                            value={additionalInstructions}
                            onChange={(e) => setAdditionalInstructions(e.target.value)}
                            placeholder={t('motivationLetter.additionalInstructionsPlaceholder')}
                            className="w-full p-3 bg-brand-white text-brand-dark border border-slate-600 focus:ring-2 focus:ring-brand-orange focus:outline-none min-h-[120px] resize-y rounded-md"
                            disabled={isLoading}
                        />
                        <p className="text-xs text-slate-600 mt-1">
                            {t('motivationLetter.additionalInstructionsHint')}
                        </p>
                    </div>

                    {error && (
                        <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
                            {error}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="flex items-center space-x-4 mt-6">
                        <button
                            onClick={handleGenerate}
                            className="px-6 py-2 border border-brand-orange hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors disabled:bg-slate-500 disabled:cursor-not-allowed flex items-center"
                            disabled={isLoading || taskStatus === 'processing'}
                        >
                            {isLoading || taskStatus === 'processing' ? (
                                <>
                                    <Loader2 className="animate-spin mr-2" size={20} />
                                    {t('motivationLetter.generatingButton')}
                                </>
                            ) : (
                                t('motivationLetter.generateButton')
                            )}
                        </button>
                        <button
                            onClick={handleClear}
                            className="px-6 py-2 border border-gray-600 hover:bg-brand-orange text-brand-dark font-semibold rounded-md transition-colors"
                            disabled={isLoading || taskStatus === 'processing'}
                        >
                            {t('motivationLetter.clearButton')}
                        </button>
                    </div>

                    {/* Display Generated Letter */}
                    {generatedLetter && (
                        <div className="mt-10">
                            <div className="flex items-center justify-between mb-4">
                                <h4 className="text-2xl font-semibold">
                                    {t('motivationLetter.generatedLetter')}
                                </h4>
                                
                            </div>
                            
                            <div className="relative p-6 bg-slate-50 border border-slate-300 rounded-md whitespace-pre-wrap">
                                <button
                                    onClick={handleCopy}
                                    className="absolute top-2 right-2 p-1.5 bg-slate-200 hover:bg-brand-orange rounded-md"
                                >
                                    {copied ? (
                                        <>
                                            <Check size={16} className="mr-2" />
                                            
                                        </>
                                    ) : (
                                        <>
                                            <Copy size={16} className="mr-2" />
                                            
                                        </>
                                    )}
                                </button>
                                {generatedLetter}
                            </div>

                            {/* Download Buttons */}
                            <div className="flex flex-wrap gap-4 mt-6">
                                <button
                                    onClick={() => handleDownload('txt')}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('motivationLetter.downloadTXT')}
                                </button>
                                <button
                                    onClick={() => handleDownload('docx')}
                                    className="px-4 py-2 bg-gray-400 text-white rounded-md hover:bg-brand-orange transition-colors flex items-center"
                                >
                                    <Download size={14} className="mr-2" />
                                    {t('motivationLetter.downloadDOCX')}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default MotivationLetter;
